import { api } from "./_client";

// iter-132: File manager API client.
export const listFiles = async (parent = "root") =>
    (await api.get(`/files`, { params: { parent } })).data;
export const listAllFolders = async () =>
    (await api.get(`/files/folders`)).data;
export const listSystemFolders = async () =>
    (await api.get(`/files/system`)).data;
export const filesUsage = async () =>
    (await api.get(`/files/usage`)).data;
export const createFolder = async (name, parent_id = "root") =>
    (await api.post(`/files/folder`, { name, parent_id })).data;
export const uploadFile = async (file, parent_id = "root", onProgress, signal) => {
    const fd = new FormData();
    fd.append("parent_id", parent_id);
    fd.append("file", file);
    const res = await api.post(`/files/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: onProgress,
        signal,
    });
    return res.data;
};

// iter-159 — superadmin-only icon swap for a folder.
export const updateFileIcon = async (fileId, icon) => (
    (await api.put(`/files/${fileId}`, { icon })).data
);

// iter-159 — superadmin-only reorder for the pinned system tiles.
export const reorderSystemFolders = async (fileIds) => (
    (await api.patch(`/admin/files/system-folders/order`, { file_ids: fileIds })).data
);

// iter-160 — superadmin uploads a custom SVG as a folder icon.
export const uploadFolderIconSvg = async (fileId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post(
        `/admin/files/${fileId}/icon-upload`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
    );
    return data;
};

export const clearFolderIconSvg = async (fileId) => (
    (await api.delete(`/admin/files/${fileId}/icon-upload`)).data
);

// iter-161 — persistent library of previously uploaded SVGs (superadmin).
export const getFolderIconLibrary = async () => (
    (await api.get(`/admin/files/icon-library`)).data
);

// iter-161 — apply a library SVG to a folder without re-uploading.
export const applyFolderIconFromLibrary = async (fileId, dataUrl) => (
    (await api.post(`/admin/files/${fileId}/icon-apply`, { data_url: dataUrl })).data
);

// iter-161 — polling counter for cross-laptop sync of icon / rename / order changes.
export const getFilesStateVersion = async () => (
    (await api.get(`/files/state-version`)).data
);

// iter-162 — bulk delete of files + folders (their whole tree gets nuked).
export const bulkDeleteFiles = async (fileIds) => (
    (await api.post(`/files/bulk-delete`, { file_ids: fileIds })).data
);

// iter-163 — restore soft-deleted files after Undo Toast click.
export const bulkRestoreFiles = async (fileIds) => (
    (await api.post(`/files/bulk-restore`, { file_ids: fileIds })).data
);

// iter-164 — Trash View: list soft-deleted files.
export const listTrashFiles = async () => (
    (await api.get(`/files/trash`)).data
);

// iter-164 — Permanent delete from Trash View.
export const bulkPermanentDeleteFiles = async (fileIds) => (
    (await api.post(`/files/bulk-permanent-delete`, { file_ids: fileIds })).data
);

// iter-162 — bulk download as ZIP preserving folder tree. Streams a blob
// so we can hand it to the browser with a real "Save As" prompt.
export const bulkDownloadFiles = async (fileIds) => {
    const res = await api.post(
        `/files/bulk-download`,
        { file_ids: fileIds },
        { responseType: "blob" },
    );
    return res.data;
};
export const renameFile = async (id, name) =>
    (await api.put(`/files/${id}`, { name })).data;
export const moveFile = async (id, parent_id) =>
    (await api.put(`/files/${id}/move`, { parent_id })).data;
export const copyFile = async (id, parent_id) =>
    (await api.post(`/files/${id}/copy`, { parent_id })).data;
export const deleteFile = async (id) =>
    (await api.delete(`/files/${id}`)).data;
// iter-167 — Downloadable URL with the auth token as a query param so
// clicking a plain <a href={downloadFileUrl(id)}> in a new tab (or on
// mobile) still authenticates. The backend's `get_current_user` reads
// `?token=` as a fallback for <img>/<a> requests that can't attach
// Authorization headers.
const _TOKEN_KEY = "planflow.auth_token";
export const downloadFileUrl = (id) => {
    let t = "";
    try { t = localStorage.getItem(_TOKEN_KEY) || ""; } catch { /* SSR / private mode */ }
    const qs = t ? `?token=${encodeURIComponent(t)}` : "";
    return `${api.defaults.baseURL}/files/${id}/download${qs}`;
};
export const previewFile = async (id) =>
    (await api.get(`/files/${id}/preview`)).data;
export const searchFiles = async (q) =>
    (await api.get(`/files/search`, { params: { q } })).data;

// Saved estimate prompts (attached to same router for tidiness).
export const listSavedPrompts = async () =>
    (await api.get(`/estimate-prompts`)).data;
export const createSavedPrompt = async (name, prompt) =>
    (await api.post(`/estimate-prompts`, { name, prompt })).data;
export const deleteSavedPrompt = async (promptId) =>
    (await api.delete(`/estimate-prompts/${promptId}`)).data;
